import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatAnimation } from './ChatAnimation';
import { cn } from '@/lib/utils';
import { Utensils, Wrench, Building2, Home, Dumbbell } from 'lucide-react';

// Types
interface Message {
    type: 'customer' | 'bot';
    text: string;
    timestamp: string;
}

interface UseCase {
    id: string;
    title: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    chatTitle: string;
    chatSubtitle: string;
    messages: Message[];
}

const USE_CASES: UseCase[] = [
    {
        id: 'plumber',
        title: 'Home Services',
        label: 'Home Services',
        icon: Wrench,
        color: 'bg-blue-600',
        chatTitle: 'Rapid Fix Plumbing',
        chatSubtitle: '24/7 Emergency Support',
        messages: [
            { type: 'customer', text: 'Help! My water heater is leaking.', timestamp: '9:05 AM' },
            { type: 'bot', text: 'I\'m sorry to hear that! Is it a major leak?', timestamp: '9:05 AM' },
            { type: 'customer', text: 'Yes, it\'s flooding the garage!', timestamp: '9:06 AM' },
            { type: 'bot', text: 'Dispatching a technician now. Turn off the main valve if safe. They\'ll arrive in ~45 mins.', timestamp: '9:06 AM' },
        ]
    },
    {
        id: 'saas',
        title: 'SaaS',
        label: 'SaaS Companies',
        icon: Building2,
        color: 'bg-indigo-600',
        chatTitle: 'CloudScale SaaS',
        chatSubtitle: 'Tech Support & Sales',
        messages: [
            { type: 'customer', text: 'How do I add a new user to my team?', timestamp: '2:30 PM' },
            { type: 'bot', text: 'You can add users in Settings > Team Management. Need the link?', timestamp: '2:30 PM' },
            { type: 'customer', text: 'No, I found it. Do you offer volume discounts?', timestamp: '2:31 PM' },
            { type: 'bot', text: 'Yes! For 20+ seats, you get 20% off. Shall I connect you with sales for a custom quote?', timestamp: '2:31 PM' },
        ]
    },
    {
        id: 'restaurant',
        title: 'Restaurants',
        label: 'Restaurants',
        icon: Utensils,
        color: 'bg-orange-500',
        chatTitle: 'Bella Cucina',
        chatSubtitle: 'Reservations & Menu',
        messages: [
            { type: 'customer', text: 'Do you have a table for 4 tonight at 7?', timestamp: '6:15 PM' },
            { type: 'bot', text: 'Let me check... Yes! We have a table available at 7:00 PM. Would you like to reserve it?', timestamp: '6:15 PM' },
            { type: 'customer', text: 'Yes please. Also, is the patio open?', timestamp: '6:16 PM' },
            { type: 'bot', text: 'Table for 4 at 7 PM confirmed! The patio is open and heated tonight. Call 555-0123 if plans change.', timestamp: '6:16 PM' },
        ]
    },
    {
        id: 'realestate',
        title: 'Real Estate',
        label: 'Real Estate',
        icon: Home,
        color: 'bg-emerald-600',
        chatTitle: 'Prestige Properties',
        chatSubtitle: 'Listings & Viewings',
        messages: [
            { type: 'customer', text: 'Is the condo on Main St still available?', timestamp: '11:20 AM' },
            { type: 'bot', text: 'Yes, 123 Main St is active! Price: $450k. Would you like to schedule a private viewing?', timestamp: '11:20 AM' },
            { type: 'customer', text: 'I\'m free this Saturday.', timestamp: '11:21 AM' },
            { type: 'bot', text: 'Perfect. I have slots at 10 AM and 1 PM. I can also have our agent call you at your registered number?', timestamp: '11:21 AM' },
        ]
    },
    {
        id: 'gym',
        title: 'Fitness',
        label: 'Fitness Centers',
        icon: Dumbbell,
        color: 'bg-rose-500',
        chatTitle: 'IronFlex Gym',
        chatSubtitle: 'Membership & Classes',
        messages: [
            { type: 'customer', text: 'What time is the evening yoga class?', timestamp: '4:10 PM' },
            { type: 'bot', text: 'Tonight\'s Vinyasa Flow starts at 6:30 PM with Sarah. 4 spots left!', timestamp: '4:10 PM' },
            { type: 'customer', text: 'Can I book a spot?', timestamp: '4:11 PM' },
            { type: 'bot', text: 'You\'re booked for 6:30 PM Vinyasa Flow! Questions? Call front desk at 555-0199.', timestamp: '4:11 PM' },
        ]
    }
];

const AUTO_PLAY_DURATION = 8500;

export const ChatCarousel = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [direction, setDirection] = useState(1);
    // First slide shows the full conversation immediately so visitors see the
    // product working on page load; later slides keep the typing animation.
    const [hasRotated, setHasRotated] = useState(false);
    const activeUseCase = USE_CASES[activeIndex];

    const goTo = useCallback((index: number) => {
        setDirection(index > activeIndex ? 1 : -1);
        setActiveIndex(index);
        setHasRotated(true);
    }, [activeIndex]);

    useEffect(() => {
        if (isPaused) return;

        const interval = setInterval(() => {
            setDirection(1);
            setActiveIndex((prev) => (prev + 1) % USE_CASES.length);
            setHasRotated(true);
        }, AUTO_PLAY_DURATION);

        return () => clearInterval(interval);
    }, [isPaused, activeIndex]);

    const slideVariants = {
        enter: (d: number) => ({ opacity: 0, x: d > 0 ? 40 : -40, scale: 0.98 }),
        center: { opacity: 1, x: 0, scale: 1 },
        exit: (d: number) => ({ opacity: 0, x: d > 0 ? -40 : 40, scale: 0.98 }),
    };

    return (
        <div className="flex flex-col items-center w-full max-w-5xl mx-auto gap-6 relative px-4">
            {/* Industry selector tabs */}
            <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-xl border border-border/40">
                {USE_CASES.map((useCase, index) => {
                    const isActive = index === activeIndex;
                    const Icon = useCase.icon;
                    return (
                        <button
                            key={useCase.id}
                            onClick={() => goTo(index)}
                            aria-label={`Show ${useCase.label} demo`}
                            className={cn(
                                "relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                                isActive
                                    ? "text-foreground"
                                    : "text-muted-foreground hover:text-foreground/80"
                            )}
                        >
                            {isActive && (
                                <motion.div
                                    layoutId="carousel-tab-bg"
                                    className="absolute inset-0 bg-background rounded-lg shadow-sm border border-border/60"
                                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                />
                            )}
                            <Icon className="w-3.5 h-3.5 relative z-10" />
                            <span className="relative z-10 hidden sm:inline">{useCase.title}</span>
                        </button>
                    );
                })}
            </div>

            {/* Chat interface */}
            <div
                className="relative w-full max-w-md mx-auto"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            >
                {/* The instant first slide renders outside AnimatePresence: flipping a
                    prop on a slide that later exits under mode="wait" stalls the exit
                    handshake and no next slide ever mounts. Once rotation starts, only
                    animated (non-instant) slides ever enter/exit the presence tree. */}
                {!hasRotated ? (
                    <ChatAnimation
                        messages={activeUseCase.messages}
                        title={activeUseCase.chatTitle}
                        subtitle={activeUseCase.chatSubtitle}
                        themeColor={activeUseCase.color}
                        instant
                    />
                ) : (
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                            key={activeIndex}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                        >
                            <ChatAnimation
                                messages={activeUseCase.messages}
                                title={activeUseCase.chatTitle}
                                subtitle={activeUseCase.chatSubtitle}
                                themeColor={activeUseCase.color}
                            />
                        </motion.div>
                    </AnimatePresence>
                )}

                {/* Progress bar */}
                <div className="flex justify-center gap-1.5 mt-5">
                    {USE_CASES.map((useCase, index) => {
                        const isActive = index === activeIndex;
                        return (
                            <button
                                key={useCase.id}
                                onClick={() => goTo(index)}
                                aria-label={`Show ${useCase.label} demo`}
                                className={cn(
                                    "relative h-1 rounded-full overflow-hidden transition-all duration-300",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                                    isActive ? "w-10" : "w-6 bg-border hover:bg-border/80"
                                )}
                            >
                                {isActive && (
                                    <div className={cn("absolute inset-0 rounded-full opacity-25", useCase.color)} />
                                )}
                                {isActive && !isPaused && (
                                    <motion.div
                                        className={cn("absolute inset-y-0 left-0 rounded-full", useCase.color)}
                                        initial={{ width: '0%' }}
                                        animate={{ width: '100%' }}
                                        transition={{ duration: AUTO_PLAY_DURATION / 1000, ease: "linear" }}
                                    />
                                )}
                                {isActive && isPaused && (
                                    <div className={cn("absolute inset-0 rounded-full", useCase.color)} />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
